import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { getSubscriber, isSupabaseConfigured, upsertSubscriber } from '../lib/supabase';
import { validateEmail } from '../utils/regexes';

const STORAGE_TOKEN_KEY = 'fortnite-dashboard:subscriber-token';
const STORAGE_EMAIL_KEY = 'fortnite-dashboard:email';
const STORAGE_WISHLIST_KEY = 'fortnite-dashboard:wishlist';

const WishlistContext = createContext({
  email: '',
  wishlist: [],
  isLoading: false,
  isSyncing: false,
  isSupabaseReady: false,
  hasNotifications: false,
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  toggleWishlist: () => {},
  isWishlisted: () => false,
  saveEmail: async () => ({ ok: false }),
  clearEmail: () => {},
});

function readStoredWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_WISHLIST_KEY);
    const items = raw ? JSON.parse(raw) : [];

    return items.map((item, index) => ({
      ...item,
      addedAt: item.addedAt || Date.now() - index * 1000,
    }));
  } catch (error) {
    return [];
  }
}

function normalizeWishlistItem(item) {
  return {
    ...item,
    addedAt: item.addedAt || Date.now(),
  };
}

function getOrCreateToken() {
  let token = localStorage.getItem(STORAGE_TOKEN_KEY);

  if (!token) {
    token = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(STORAGE_TOKEN_KEY, token);
  }

  return token;
}

export function WishlistProvider({ children }) {
  const [token] = useState(getOrCreateToken);
  const [email, setEmail] = useState(() => localStorage.getItem(STORAGE_EMAIL_KEY) || '');
  const [wishlist, setWishlist] = useState(readStoredWishlist);
  const [isLoading, setIsLoading] = useState(isSupabaseConfigured);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');

  const persistWishlist = useCallback((items) => {
    setWishlist(items);
    localStorage.setItem(STORAGE_WISHLIST_KEY, JSON.stringify(items));
  }, []);

  const syncToSupabase = useCallback(async (nextWishlist, nextEmail = email, notifyEnabled = true) => {
    if (!isSupabaseConfigured) return null;

    setIsSyncing(true);
    setSyncError('');

    try {
      const { data, error } = await upsertSubscriber({
        token,
        wishlist: nextWishlist,
        email: nextEmail || null,
        notifyEnabled,
      });

      if (error) throw error;

      const subscriber = Array.isArray(data) ? data[0] : data;

      if (subscriber?.email) {
        setEmail(subscriber.email);
        localStorage.setItem(STORAGE_EMAIL_KEY, subscriber.email);
      }

      return subscriber;
    } catch (error) {
      console.error('Failed to sync wishlist', error);
      setSyncError(error.message || 'Could not save to Supabase');
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [email, token]);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadSubscriber() {
      setIsLoading(true);

      try {
        const { data, error } = await getSubscriber(token);

        if (error) throw error;

        const subscriber = Array.isArray(data) ? data[0] : data;

        if (cancelled || !subscriber) return;

        if (subscriber.wishlist?.length) {
          persistWishlist(subscriber.wishlist);
        }

        if (subscriber.email) {
          setEmail(subscriber.email);
          localStorage.setItem(STORAGE_EMAIL_KEY, subscriber.email);
        }
      } catch (error) {
        console.error('Failed to load subscriber', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadSubscriber();

    return () => {
      cancelled = true;
    };
  }, [persistWishlist, token]);

  const addToWishlist = useCallback((item) => {
    setWishlist((current) => {
      if (current.some((entry) => entry.id === item.id)) return current;

      const nextWishlist = [...current, normalizeWishlistItem(item)];
      localStorage.setItem(STORAGE_WISHLIST_KEY, JSON.stringify(nextWishlist));
      syncToSupabase(nextWishlist);
      return nextWishlist;
    });
  }, [syncToSupabase]);

  const removeFromWishlist = useCallback((item) => {
    setWishlist((current) => {
      const nextWishlist = current.filter((entry) => entry.id !== item.id);
      localStorage.setItem(STORAGE_WISHLIST_KEY, JSON.stringify(nextWishlist));
      syncToSupabase(nextWishlist);
      return nextWishlist;
    });
  }, [syncToSupabase]);

  const toggleWishlist = useCallback((item) => {
    setWishlist((current) => {
      const isListed = current.some((entry) => entry.id === item.id);

      if (isListed) {
        const nextWishlist = current.filter((entry) => entry.id !== item.id);
        localStorage.setItem(STORAGE_WISHLIST_KEY, JSON.stringify(nextWishlist));
        syncToSupabase(nextWishlist);
        return nextWishlist;
      }

      const nextWishlist = [...current, normalizeWishlistItem(item)];
      localStorage.setItem(STORAGE_WISHLIST_KEY, JSON.stringify(nextWishlist));
      syncToSupabase(nextWishlist);
      return nextWishlist;
    });
  }, [syncToSupabase]);

  const isWishlisted = useCallback((itemId) => (
    wishlist.some((entry) => entry.id === itemId)
  ), [wishlist]);

  const saveEmail = useCallback(async (nextEmail) => {
    const trimmedEmail = nextEmail.trim();

    if (!validateEmail(trimmedEmail)) {
      return { ok: false, error: 'Enter a valid email address.' };
    }

    if (!isSupabaseConfigured) {
      setEmail(trimmedEmail);
      localStorage.setItem(STORAGE_EMAIL_KEY, trimmedEmail);
      return { ok: true, offline: true };
    }

    const subscriber = await syncToSupabase(wishlist, trimmedEmail, true);

    if (!subscriber) {
      return { ok: false, error: syncError || 'Could not save your email.' };
    }

    return { ok: true };
  }, [syncError, syncToSupabase, wishlist]);

  const clearEmail = useCallback(() => {
    setEmail('');
    localStorage.removeItem(STORAGE_EMAIL_KEY);
    syncToSupabase(wishlist, '', false);
  }, [syncToSupabase, wishlist]);

  const value = useMemo(() => ({
    email,
    wishlist,
    isLoading,
    isSyncing,
    syncError,
    isSupabaseReady: isSupabaseConfigured,
    hasNotifications: Boolean(email),
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isWishlisted,
    saveEmail,
    clearEmail,
  }), [
    addToWishlist,
    clearEmail,
    email,
    isLoading,
    isSyncing,
    isWishlisted,
    removeFromWishlist,
    saveEmail,
    syncError,
    toggleWishlist,
    wishlist,
  ]);

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  return useContext(WishlistContext);
}
